/* 
██╗██╗░░░██╗  ██████╗░██╗░██████╗░██╗████████╗░█████╗░██╗░░░░░
██║██║░░░██║  ██╔══██╗██║██╔════╝░██║╚══██╔══╝██╔══██╗██║░░░░░
██║██║░░░██║  ██║░░██║██║██║░░██╗░██║░░░██║░░░███████║██║░░░░░
██║██║░░░██║  ██║░░██║██║██║░░╚██╗██║░░░██║░░░██╔══██║██║░░░░░
██║╚██████╔╝  ██████╔╝██║╚██████╔╝██║░░░██║░░░██║░░██║███████╗
╚═╝░╚═════╝░  ╚═════╝░╚═╝░╚═════╝░╚═╝░░░╚═╝░░░╚═╝░░╚═╝╚══════╝
╔════════════════════════════════════════════════════════════╗
║              EQUIPO DE DESARROLLO DE SOFTWARE              ║
║                   MODULO DESARROLLADO POR                  ║
╟┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄╢
║ Victor Manuel Calle Londoño                                ║
║ victor.calle@est.iudigital.edu.co                          ║
╚════════════════════════════════════════════════════════════╝
*/

/**
 * Componente modal para crear una nueva fuente de datos.
 *
 * Este componente renderiza un formulario modal que permite a los usuarios crear
 * nuevas fuentes de datos. Soporta diferentes tipos de fuentes (CSV, JSON, EXCEL,
 * MySQL, PostgreSQL) y permite la carga de archivos. Incluye validación de campos,
 * manejo de estados, y la capacidad de subir una imagen strip opcional para PKPass.
 * La imagen strip debe ser en formato PNG y no superar los 2MB.
 *
 * Características principales:
 * - Selección del tipo de fuente de datos
 * - Carga de archivos (CSV, JSON, EXCEL)
 * - Configuración de conexiones a bases de datos (MySQL, PostgreSQL)
 * - Carga opcional de imagen strip para PKPass (PNG, máx 2MB)
 * - Validación en tiempo real de campos
 * - Configuración de tiempos para invitados
 *
 * @component
 * @author Victor Manuel Calle Londoño <victor.calle@est.iudigital.edu.co>
 * @date [Fecha de creación: 2023-10-18]
 * @updated [Fecha de última modificación: 2025-10-21]
 * @updatedBy Eulicer Zapata <eulicer.zapata@iudigital.edu.co>
 */
import {
  Box,
  Divider,
  FormControl,
  FormHelperText,
  Icon,
  MenuItem,
  Modal,
  Select,
  Typography,
  styled,
  Card,
} from "@mui/material";
import colors from "assets/theme/base/colors";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import PropTypes from "prop-types";
import React from "react";
import DataTable from "examples/Tables/DataTable";
import TimeSelectComponent from "components/TimeSelect/TimeSelectComponent";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import ToolTip from "components/ToolTip";

export function SourceNew({
  open,
  handleClose,
  isFormModified,
  isDisabled,
  getFieldValue,
  sourceTypes,
  carnetTypes,
  handleFieldChange,
  handleSubmit,
  handleEstablishConnection,
  columData,
  statusConexion,
  tableRows,
  isFieldValid,
  handleStripImageChange,
  fieldErrorsNew,
}) {
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "90%",
    maxWidth: "800px",
    borderRadius: "20px",
    boxShadow: 24,
    p: 4,
    maxHeight: "95vh",
  };

  const content = {
    margin: "0.5rem 0",
    padding: "0 0.5rem",
    display: "grid",
    width: "100%",
    gridTemplateColumns: {
      xs: "1fr",
      md: "repeat(2, 1fr)",
    },
    gap: "12px",
    alignItems: "start",
  };

  const boxCommonStyles = {
    width: "100%",
    padding: "8px",
    borderRadius: "8px",
    transition: "all 0.2s ease-in-out",
    display: "flex",
    flexDirection: "column",
  };

  const inputStyles = {
    width: "100%",
    "& .MuiInputBase-root": {
      minHeight: "45px",
    },
    "& .MuiInputBase-input": {
      padding: "12px 14px",
    },
  };

  const selectStyles = {
    width: "100%",
    minHeight: "45px",
    "& .MuiSelect-select": {
      padding: "12px 14px",
    },
  };

  const labelStyles = {
    color: "black",
    fontWeight: 500,
    marginBottom: "4px",
    fontSize: "0.95rem",
    display: "block",
  };

  const tableColumns = [
    { Header: "BD Nativa", accessor: "staticValue" },
    { Header: "BD Entrante", accessor: "select" },
  ];

  const HtmlTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: "#f5f5f9",
      color: "rgba(0, 0, 0, 0.87)",
      maxWidth: 220,
      fontSize: theme.typography.pxToRem(12),
      border: "1px solid #dadde9",
    },
  }));

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Card sx={style}>
        <MDBox
          mt={-7}
          color="white"
          bgColor="info"
          variant="gradient"
          borderRadius="lg"
          p={2}
          sx={{ display: "flex", justifyContent: "flex-end" }}
        >
          <Typography id="modal-modal-title" component="h2">
            Nueva fuente de datos
          </Typography>
          <Icon
            sx={{
              fontSize: "2em",
              stroke: "currentColor",
              strokeWidth: "2px",
              cursor: "pointer",
              marginLeft: "auto",
            }}
            onClick={handleClose}
          >
            close
          </Icon>
        </MDBox>
        <Divider variant="middle" />
        <div style={{ maxHeight: "calc(95vh - 200px)", overflowY: "auto", padding: "8px 0" }}>
          <form onSubmit={handleSubmit}>
            <MDBox
              sx={content}
              style={{
                justifyContent: "space-around",
              }}
            >
              <Box sx={boxCommonStyles}>
                <Typography id="modal-modal-description" sx={labelStyles}>
                  Tipo de fuente *
                </Typography>
                <FormControl sx={{ minWidth: "100%" }} error={!isFieldValid.name}>
                  <Select
                    labelId="labelId-select-tipo-de-fuente"
                    id="select-tipo-de-fuente"
                    value={getFieldValue("name")}
                    onChange={(e) => handleFieldChange("name", e.target.value)}
                    sx={selectStyles}
                    disabled={isDisabled}
                    displayEmpty
                  >
                    <MenuItem value={""}>Seleccione...</MenuItem>
                    {sourceTypes
                      ? sourceTypes.map((source) => {
                          return (
                            <MenuItem
                              key={source.id}
                              value={source.name}
                              disabled={source.name === "MySQL" || source.name === "PostgreSQL"}
                            >
                              {source.name}
                            </MenuItem>
                          );
                        })
                      : []}
                  </Select>
                  {!isFieldValid.name && <FormHelperText>Tipo de fuente requerida</FormHelperText>}
                </FormControl>
              </Box>
              <Box sx={boxCommonStyles}>
                <Typography id="modal-modal-description" sx={labelStyles}>
                  Nombre de la fuente *
                </Typography>
                <MDInput
                  disabled={isDisabled}
                  style={inputStyles}
                  label=""
                  value={getFieldValue("source_name")}
                  inputProps={{ maxLength: 255 }}
                  error={!isFieldValid.source_name || !!fieldErrorsNew?.source_name}
                  helperText={
                    fieldErrorsNew?.source_name
                      ? fieldErrorsNew.source_name
                      : !isFieldValid.source_name
                      ? "No puede estar vacio!"
                      : ""
                  }
                  FormHelperTextProps={{
                    className:
                      !isFieldValid.source_name || fieldErrorsNew?.source_name ? "Mui-error" : "",
                  }}
                  onChange={(e) => handleFieldChange("source_name", e.target.value)}
                />
                <Typography
                  variant="caption"
                  sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
                >
                  Máximo 255 caracteres
                </Typography>
              </Box>
              <Box sx={boxCommonStyles}>
                <Typography id="modal-modal-description" sx={labelStyles}>
                  Tipo de carnet *
                </Typography>
                <FormControl sx={{ minWidth: "100%" }} error={!isFieldValid.perfil}>
                  <Select
                    labelId="demo-simple-select-autowidth-label"
                    id="demo-simple-select-autowidth"
                    value={getFieldValue("perfil")}
                    onChange={(e) => handleFieldChange("perfil", e.target.value)}
                    sx={selectStyles}
                    disabled={isDisabled}
                    displayEmpty
                  >
                    <MenuItem value={""}>Seleccione...</MenuItem>
                    {carnetTypes
                      ? carnetTypes.map((carnet) => {
                          return (
                            <MenuItem key={carnet.id} value={carnet.name}>
                              {carnet.name}
                            </MenuItem>
                          );
                        })
                      : []}
                  </Select>
                  {!isFieldValid.perfil && (
                    <FormHelperText>Tipo de carnet requerido</FormHelperText>
                  )}
                </FormControl>
              </Box>
              <Box sx={boxCommonStyles}>
                <Typography id="modal-modal-description" sx={labelStyles}>
                  Descripción de la fuente
                </Typography>
                <MDInput
                  disabled={isDisabled}
                  style={{
                    ...inputStyles,
                    "& .MuiInputBase-root": {
                      minHeight: "80px",
                      alignItems: "flex-start",
                    },
                  }}
                  label=""
                  multiline
                  rows={2}
                  value={getFieldValue("source_description")}
                  onChange={(e) => handleFieldChange("source_description", e.target.value)}
                />
              </Box>
              {/* Campos de ubicación solo para Invitado y Visitante */}
              {(getFieldValue("perfil") === "Invitado" ||
                getFieldValue("perfil") === "Visitante") && (
                <>
                  <Box sx={boxCommonStyles}>
                    <Typography id="modal-modal-description" sx={labelStyles}>
                      Lugar del evento *
                      <ToolTip message="Ingresa el nombre del lugar donde se realizará el evento (máximo 25 caracteres). Presiona Enter en el campo vacío para autocompletar con 'IU Digital de Antioquia'." />
                    </Typography>
                    <MDInput
                      disabled={isDisabled}
                      style={inputStyles}
                      label=""
                      placeholder="IU Digital de Antioquia (Enter para autocompletar)"
                      value={getFieldValue("location")}
                      inputProps={{ maxLength: 25 }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !getFieldValue("location")) {
                          e.preventDefault();
                          handleFieldChange("location", "IU Digital de Antioquia");
                        }
                      }}
                      onChange={(e) => handleFieldChange("location", e.target.value)}
                      error={!isFieldValid.location || !!fieldErrorsNew?.location}
                      helperText={
                        fieldErrorsNew?.location
                          ? fieldErrorsNew.location
                          : !isFieldValid.location
                          ? "Caracteres no válidos en Lugar del evento"
                          : ""
                      }
                      FormHelperTextProps={{
                        className:
                          !isFieldValid.location || fieldErrorsNew?.location ? "Mui-error" : "",
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
                    >
                      Máximo 25 caracteres • Permitidos: letras, números, comas, puntos, guiones.
                    </Typography>
                  </Box>
                  <Box sx={boxCommonStyles}>
                    <Typography id="modal-modal-description" sx={labelStyles}>
                      Dirección del lugar *
                      <ToolTip message="Ingresa la dirección completa del lugar del evento (máximo 100 caracteres). Presiona Enter en el campo vacío para autocompletar con la dirección del Centro Cívico de Antioquia." />
                    </Typography>
                    <MDInput
                      disabled={isDisabled}
                      style={inputStyles}
                      label=""
                      placeholder="Centro Cívico de Antioquia Plaza de la Libertad, Cra. 55 #42-90 Interior 0101, Medellín, Antioquia"
                      value={getFieldValue("address")}
                      inputProps={{ maxLength: 100 }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !getFieldValue("address")) {
                          handleFieldChange(
                            "address",
                            "Centro Cívico de Antioquia Plaza de la Libertad, Cra. 55 #42-90 Interior 0101, Medellín, Antioquia"
                          );
                        }
                      }}
                      onChange={(e) => handleFieldChange("address", e.target.value)}
                      error={!isFieldValid.address || !!fieldErrorsNew?.address}
                      helperText={
                        fieldErrorsNew?.address
                          ? fieldErrorsNew.address
                          : !isFieldValid.address
                          ? "Caracteres no válidos en Dirección"
                          : ""
                      }
                      FormHelperTextProps={{
                        className:
                          !isFieldValid.address || fieldErrorsNew?.address ? "Mui-error" : "",
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
                    >
                      Máximo 100 caracteres • Permitidos: letras, números, comas, puntos, guiones.
                    </Typography>
                  </Box>
                </>
              )}
              {getFieldValue("name") === "CSV" && (
                <Box sx={{ ...boxCommonStyles, alignSelf: "start" }}>
                  <Box sx={{ display: "flex", alignItems: "center", marginBottom: "4px", gap: 2 }}>
                    <Typography
                      id="modal-modal-description"
                      sx={{ ...labelStyles, marginBottom: 0 }}
                    >
                      Fuente: Archivo csv
                    </Typography>
                    <a
                      href="/assets/Plantilla CSV.csv"
                      download="Plantilla CSV.csv"
                      target="_self"
                      title="Descarga la plantilla de ejemplo"
                      style={{
                        textDecoration: "none",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <Icon
                        sx={{
                          fontSize: "1.5rem",
                          color: colors.info.main,
                        }}
                      >
                        download
                      </Icon>
                      <Typography
                        sx={{
                          fontSize: "0.8rem",
                          color: colors.info.main,
                          fontWeight: "500",
                        }}
                      >
                        Descargar plantilla
                      </Typography>
                      <ToolTip message="Haz clic en el botón de descarga para obtener un archivo CSV de ejemplo que puedes usar como plantilla para cargar tus datos." />
                    </a>
                  </Box>

                  <MDInput
                    disabled={isDisabled}
                    label=""
                    type="file"
                    error={!isFieldValid.CSV}
                    helperText={!isFieldValid.CSV ? "El archivo no es de extension CSV" : ""}
                    FormHelperTextProps={{
                      className: !isFieldValid.CSV ? "Mui-error" : "",
                    }}
                    onChange={(e) => {
                      const selectedFile = e.target.files[0];
                      handleFieldChange("source", selectedFile);
                    }}
                  ></MDInput>
                </Box>
              )}
              {getFieldValue("name") === "JSON" && (
                <Box sx={boxCommonStyles}>
                  <Box sx={{ display: "flex", alignItems: "center", marginBottom: "4px", gap: 2 }}>
                    <Typography
                      id="modal-modal-description"
                      sx={{ ...labelStyles, marginBottom: 0 }}
                    >
                      Fuente: Archivo json
                    </Typography>
                    <a
                      href="/assets/Plantilla JSON.json"
                      download="Plantilla JSON.json"
                      target="_self"
                      title="Descarga la plantilla de ejemplo"
                      style={{
                        textDecoration: "none",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <Icon
                        sx={{
                          fontSize: "1.5rem",
                          color: colors.info.main,
                        }}
                      >
                        download
                      </Icon>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          color: colors.info.main,
                          fontWeight: "500",
                        }}
                      >
                        Descargar plantilla
                      </Typography>
                      <ToolTip message="Haz clic en el botón de descarga para obtener un archivo JSON de ejemplo que puedes usar como plantilla para cargar tus datos." />
                    </a>
                  </Box>

                  <MDInput
                    disabled={isDisabled}
                    label=""
                    type="file"
                    error={!isFieldValid.JSON}
                    helperText={!isFieldValid.JSON ? "El archivo no es de extension .json" : ""}
                    FormHelperTextProps={{
                      className: !isFieldValid.JSON ? "Mui-error" : "",
                    }}
                    onChange={(e) => {
                      const selectedFile = e.target.files[0];
                      handleFieldChange("source", selectedFile);
                    }}
                  ></MDInput>
                </Box>
              )}
              {getFieldValue("name") === "EXCEL" && (
                <Box sx={boxCommonStyles}>
                  <Box sx={{ display: "flex", alignItems: "center", marginBottom: "4px", gap: 2 }}>
                    <Typography
                      id="modal-modal-description"
                      sx={{ ...labelStyles, marginBottom: 0 }}
                    >
                      Fuente: Archivo excel
                    </Typography>
                    <a
                      href="/assets/Plantilla Excel.xlsx"
                      download="Plantilla Excel.xlsx"
                      target="_self"
                      title="Descarga la plantilla de ejemplo"
                      style={{
                        textDecoration: "none",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      <Icon
                        sx={{
                          fontSize: "1.5rem",
                          color: colors.info.main,
                        }}
                      >
                        download
                      </Icon>
                      <Typography
                        sx={{
                          fontSize: "0.85rem",
                          color: colors.info.main,
                          fontWeight: "500",
                        }}
                      >
                        Descargar plantilla
                      </Typography>
                      <ToolTip message="Haz clic en el botón de descarga para obtener un archivo EXCEL de ejemplo que puedes usar como plantilla para cargar tus datos." />
                    </a>
                  </Box>

                  <MDInput
                    disabled={isDisabled}
                    label=""
                    type="file"
                    error={!isFieldValid.EXCEL}
                    helperText={
                      !isFieldValid.EXCEL ? "El archivo no es de extension .xlsx o .xls" : ""
                    }
                    FormHelperTextProps={{
                      className: !isFieldValid.EXCEL ? "Mui-error" : "",
                    }}
                    onChange={(e) => {
                      const selectedFile = e.target.files[0];
                      handleFieldChange("source", selectedFile);
                    }}
                  ></MDInput>
                </Box>
              )}
              {getFieldValue("name") === "MySQL" && (
                <>
                  <Box sx={boxCommonStyles}>
                    <Typography id="modal-modal-description" sx={labelStyles}>
                      Url de la base de datos
                    </Typography>
                    <>
                      <MDInput
                        disabled={isDisabled}
                        style={inputStyles}
                        label=""
                        type="text"
                        placeholder="Url de la base de datos"
                        value={getFieldValue("db_host")}
                        error={!isFieldValid.db_host}
                        helperText={!isFieldValid.db_host ? "Ingresa la URl" : ""}
                        FormHelperTextProps={{
                          className: !isFieldValid.db_host ? "Mui-error" : "",
                        }}
                        onChange={(e) => handleFieldChange("db_host", e.target.value)}
                      ></MDInput>
                    </>
                  </Box>
                  <Box sx={boxCommonStyles}>
                    <Typography id="modal-modal-description" sx={labelStyles}>
                      Puerto de la base de datos
                    </Typography>
                    <>
                      <MDInput
                        disabled={isDisabled}
                        style={inputStyles}
                        label=""
                        type="text"
                        placeholder="3306"
                        value={getFieldValue("port")}
                        error={!isFieldValid.port}
                        helperText={!isFieldValid.port ? "Solo números" : ""}
                        FormHelperTextProps={{
                          className: !isFieldValid.port ? "Mui-error" : "",
                        }}
                        onChange={(e) => handleFieldChange("port", e.target.value)}
                      ></MDInput>
                    </>
                  </Box>
                  <Box sx={boxCommonStyles}>
                    <Typography id="modal-modal-description" sx={labelStyles}>
                      Nombre de usuario
                    </Typography>
                    <>
                      <MDInput
                        disabled={isDisabled}
                        style={inputStyles}
                        label=""
                        type="text"
                        placeholder="root"
                        value={getFieldValue("db_username")}
                        error={!isFieldValid.db_username}
                        helperText={!isFieldValid.db_username ? "No puede estar vacio!" : ""}
                        FormHelperTextProps={{
                          className: !isFieldValid.db_username ? "Mui-error" : "",
                        }}
                        onChange={(e) => handleFieldChange("db_username", e.target.value)}
                      ></MDInput>
                    </>
                  </Box>
                  <Box sx={boxCommonStyles}>
                    <Typography id="modal-modal-description" sx={labelStyles}>
                      Contraseña
                    </Typography>
                    <>
                      <MDInput
                        disabled={isDisabled}
                        style={inputStyles}
                        label=""
                        type="password"
                        placeholder="********"
                        value={getFieldValue("db_password")}
                        onChange={(e) => handleFieldChange("db_password", e.target.value)}
                      ></MDInput>
                    </>
                  </Box>
                  <Box sx={boxCommonStyles}>
                    <Typography id="modal-modal-description" sx={labelStyles}>
                      Nombre de la base de datos
                    </Typography>
                    <>
                      <MDInput
                        disabled={isDisabled}
                        style={inputStyles}
                        label=""
                        type="text"
                        placeholder="Nombre de la base de datos"
                        value={getFieldValue("nameDB")}
                        error={!isFieldValid.nameDB}
                        helperText={!isFieldValid.nameDB ? "No puede estar vacio!" : ""}
                        FormHelperTextProps={{
                          className: !isFieldValid.nameDB ? "Mui-error" : "",
                        }}
                        onChange={(e) => handleFieldChange("nameDB", e.target.value)}
                      ></MDInput>
                    </>
                  </Box>
                  <Box sx={boxCommonStyles}>
                    <Typography id="modal-modal-description" sx={labelStyles}>
                      Nombre de la tabla
                    </Typography>
                    <>
                      <MDInput
                        disabled={isDisabled}
                        style={inputStyles}
                        label=""
                        type="text"
                        placeholder="Nombre de la tabla"
                        value={getFieldValue("nameTable")}
                        error={!isFieldValid.nameTable}
                        helperText={!isFieldValid.nameTable ? "No puede estar vacio!" : ""}
                        FormHelperTextProps={{
                          className: !isFieldValid.nameTable ? "Mui-error" : "",
                        }}
                        onChange={(e) => handleFieldChange("nameTable", e.target.value)}
                      ></MDInput>
                    </>
                  </Box>
                  <Box sx={boxCommonStyles}>
                    <Typography id="modal-modal-description" sx={labelStyles}>
                      Establecer conexión
                    </Typography>
                    <>
                      <MDButton onClick={handleEstablishConnection} color="info">
                        Aplicar
                      </MDButton>
                    </>
                  </Box>
                  {statusConexion && <></>}
                  <Box sx={boxCommonStyles}>
                    <Typography id="modal-modal-description" sx={labelStyles}>
                      Relacione los campos de nuestra base de datos con la elegida:
                    </Typography>
                    <DataTable
                      sx={{ border: "1px solid black" }}
                      table={{
                        columns: tableColumns,
                        rows: tableRows,
                      }}
                      canSearch={false}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                    />
                  </Box>
                </>
              )}
              {getFieldValue("name") === "PostgreSQL" && (
                <>
                  <Box sx={boxCommonStyles}>
                    <Typography id="modal-modal-description" sx={labelStyles}>
                      Url de la base de datos
                    </Typography>
                    <>
                      <MDInput
                        disabled={isDisabled}
                        style={inputStyles}
                        label=""
                        type="text"
                        placeholder="Url de la base de datos"
                        value={getFieldValue("db_host")}
                        error={!isFieldValid.db_host}
                        helperText={!isFieldValid.db_host ? "Ingresa la URl" : ""}
                        FormHelperTextProps={{
                          className: !isFieldValid.db_host ? "Mui-error" : "",
                        }}
                        onChange={(e) => handleFieldChange("db_host", e.target.value)}
                      ></MDInput>
                    </>
                  </Box>
                  <Box sx={boxCommonStyles}>
                    <Typography id="modal-modal-description" sx={labelStyles}>
                      Puerto de la base de datos
                    </Typography>
                    <>
                      <MDInput
                        disabled={isDisabled}
                        style={inputStyles}
                        label=""
                        type="text"
                        placeholder="5032"
                        value={getFieldValue("port")}
                        error={!isFieldValid.port}
                        helperText={!isFieldValid.port ? "Solo números" : ""}
                        FormHelperTextProps={{
                          className: !isFieldValid.port ? "Mui-error" : "",
                        }}
                        onChange={(e) => handleFieldChange("port", e.target.value)}
                      ></MDInput>
                    </>
                  </Box>
                  <Box sx={boxCommonStyles}>
                    <Typography id="modal-modal-description" sx={labelStyles}>
                      Nombre de usuario
                    </Typography>
                    <>
                      <MDInput
                        disabled={isDisabled}
                        style={inputStyles}
                        label=""
                        type="text"
                        placeholder="Postgres"
                        value={getFieldValue("db_username")}
                        error={!isFieldValid.db_username}
                        helperText={!isFieldValid.db_username ? "No puede estar vacio!" : ""}
                        FormHelperTextProps={{
                          className: !isFieldValid.db_username ? "Mui-error" : "",
                        }}
                        onChange={(e) => handleFieldChange("db_username", e.target.value)}
                      ></MDInput>
                    </>
                  </Box>
                  <Box sx={boxCommonStyles}>
                    <Typography id="modal-modal-description" sx={labelStyles}>
                      Contraseña
                    </Typography>
                    <>
                      <MDInput
                        disabled={isDisabled}
                        style={inputStyles}
                        label=""
                        type="password"
                        placeholder="********"
                        value={getFieldValue("db_password")}
                        onChange={(e) => handleFieldChange("db_password", e.target.value)}
                      ></MDInput>
                    </>
                  </Box>
                  <Box sx={boxCommonStyles}>
                    <Typography id="modal-modal-description" sx={labelStyles}>
                      Nombre de la base de datos
                    </Typography>
                    <>
                      <MDInput
                        disabled={isDisabled}
                        style={inputStyles}
                        label=""
                        type="text"
                        placeholder="Nombre de la base de datos"
                        value={getFieldValue("nameDB")}
                        error={!isFieldValid.nameDB}
                        helperText={!isFieldValid.nameDB ? "No puede estar vacio!" : ""}
                        FormHelperTextProps={{
                          className: !isFieldValid.nameDB ? "Mui-error" : "",
                        }}
                        onChange={(e) => handleFieldChange("nameDB", e.target.value)}
                      ></MDInput>
                    </>
                  </Box>
                  <Box sx={boxCommonStyles}>
                    <Typography id="modal-modal-description" sx={labelStyles}>
                      Nombre de la tabla
                    </Typography>
                    <>
                      <MDInput
                        disabled={isDisabled}
                        style={inputStyles}
                        label=""
                        type="text"
                        placeholder="Nombre de la tabla"
                        value={getFieldValue("nameTable")}
                        onChange={(e) => handleFieldChange("nameTable", e.target.value)}
                      ></MDInput>
                    </>
                  </Box>
                  <Box sx={boxCommonStyles}>
                    <Typography id="modal-modal-description" sx={labelStyles}>
                      Establecer conexión
                    </Typography>
                    <>
                      <MDButton onClick={handleEstablishConnection} color="info">
                        Aplicar
                      </MDButton>
                    </>
                  </Box>
                  {statusConexion && <></>}
                  <Box sx={boxCommonStyles}>
                    <Typography id="modal-modal-description" sx={labelStyles}>
                      Relacione los campos de nuestra base de datos con la elegida:
                    </Typography>
                    <DataTable
                      sx={{ border: "1px solid black" }}
                      table={{
                        columns: tableColumns,
                        rows: tableRows,
                      }}
                      canSearch={false}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                    />
                  </Box>
                </>
              )}
              {/* Campo para cargar imagen strip - obligatorio para Invitado o Visitante */}
              {(getFieldValue("perfil") === "Invitado" ||
                getFieldValue("perfil") === "Visitante") && (
                <Box sx={{ ...boxCommonStyles, alignSelf: "start" }}>
                  <Typography id="modal-modal-description" sx={labelStyles}>
                    Imagen de banner PKPass *
                    <ToolTip message="Sube una imagen en formato PNG que se mostrará como franja en el pkpass del Wallet. Dimensiones recomendadas: 423 × 112 píxeles • Formato PNG • Tamaño máximo 2MB" />
                  </Typography>
                  <MDInput
                    disabled={isDisabled}
                    label=""
                    type="file"
                    required
                    inputProps={{ accept: ".png" }}
                    onChange={(e) => {
                      const selectedFile = e.target.files[0];
                      const resetInput = () => {
                        e.target.value = "";
                      };
                      handleStripImageChange(selectedFile, resetInput);
                    }}
                  />
                </Box>
              )}
              {getFieldValue("perfil") === "Invitado" && (
                <Box sx={{ ...boxCommonStyles, gridColumn: { xs: "1", md: "1 / -1" } }}>
                  <Typography
                    id="modal-modal-description"
                    sx={{
                      color: "black",
                      fontWeight: 500,
                      fontSize: "0.95rem",
                      marginBottom: "8px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                  >
                    Establezca el tiempo permitido para habilitar el ingreso de los invitados
                    <HtmlTooltip
                      title={
                        <React.Fragment>
                          {
                            "Especifica cuántas horas y minutos antes y después del evento estará permitido registrar el ingreso de los invitados."
                          }
                        </React.Fragment>
                      }
                    >
                      <HelpOutlineIcon
                        sx={{ ml: 1, fontSize: 18, color: "info.main", cursor: "pointer" }}
                      />
                    </HtmlTooltip>
                  </Typography>
                  <TimeSelectComponent
                    sx={{ justifyContent: "center" }}
                    label="Check In Time"
                    hoursIn={getFieldValue("check_in_time")}
                    hoursOut={getFieldValue("check_out_time")}
                    minutesIn={getFieldValue("check_in_minute")}
                    minutesOut={getFieldValue("check_out_minute")}
                    onTimeChange={handleFieldChange}
                  />
                </Box>
              )}
            </MDBox>
          </form>
        </div>
        <Divider variant="middle" />

        <MDBox
          style={{
            display: "flex",
            justifyContent: "space-around",
          }}
          m={-0.5}
          mb={-2}
        >
          <MDButton color="error" onClick={handleClose}>
            Cancelar
          </MDButton>
          <MDButton
            onClick={handleSubmit}
            type="submit"
            disabled={
              isDisabled ||
              isFormModified ||
              (isFieldValid && Object.values(isFieldValid).some((v) => v === false))
            }
            color="success"
          >
            Guardar
          </MDButton>
        </MDBox>
      </Card>
    </Modal>
  );
}

SourceNew.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  isDisabled: PropTypes.bool,
  isFormModified: PropTypes.bool,
  getFieldValue: PropTypes.func,
  sourceTypes: PropTypes.array,
  carnetTypes: PropTypes.array,
  handleFieldChange: PropTypes.func,
  handleSubmit: PropTypes.func,
  handleEstablishConnection: PropTypes.func,
  columData: PropTypes.array,
  statusConexion: PropTypes.bool,
  tableRows: PropTypes.array,
  isFieldValid: PropTypes.object,
  handleStripImageChange: PropTypes.func,
  fieldErrorsNew: PropTypes.object,
};
